// src/services/email/EmailPreviewService.ts

import { EmailVariables, EmailPreview } from './types';
import { unifiedEmailTemplate } from './templates/unifiedEmail';
import { emailConfig } from '../config/emailConfig';

export class EmailPreviewService {
  /**
   * Generiert eine vollständige E-Mail-Vorschau mit allen Variablen
   * Diese wird sowohl für die UI-Anzeige als auch für OpenAI verwendet
   */
  generatePreview(
    variables: EmailVariables,
    attachments?: Array<{ filename: string; size?: number }>
  ): EmailPreview {
    // Verwende das unified Template
    const template = unifiedEmailTemplate;
    
    // Generiere die E-Mail-Inhalte
    const subject = template.subject(variables);
    const bodyHtml = template.bodyHtml(variables);
    const bodyText = template.bodyText(variables);
    
    // Bestimme Empfänger
    const to = variables.customerEmail || 'kunde@example.com';
    
    // Sender-Info
    const from = `${emailConfig.sender.name} <${emailConfig.sender.email}>`;
    
    // CC-Empfänger
    const cc = variables.customerEmailCc || undefined;
    
    return {
      subject,
      bodyHtml,
      bodyText,
      to,
      from,
      cc,
      attachments: attachments || []
    };
  }

  /**
   * Extrahiert den reinen Text aus HTML für die OpenAI-Optimierung
   */
  extractTextFromHtml(html: string): string {
    // Entferne HTML-Tags für Textextraktion
    let text = html.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n\n');
    text = text.replace(/<\/div>/gi, '\n');
    text = text.replace(/<[^>]*>/g, '');
    
    // Bereinige mehrfache Leerzeilen
    text = text.replace(/\n{3,}/g, '\n\n');
    
    // Trimme Whitespace
    return text.trim();
  }

  /**
   * Markiert einen Textbereich für die OpenAI-Optimierung
   * Fügt Kontext-Marker hinzu, damit OpenAI weiß, welcher Teil optimiert werden soll
   */
  prepareTextForOptimization(
    fullEmailText: string,
    customMessage: string
  ): { contextText: string; markedSection: string } {
    // Finde die Position des Custom Message im vollständigen Text
    const customMessagePosition = fullEmailText.indexOf(customMessage);
    
    if (customMessagePosition === -1) {
      // Falls Custom Message nicht gefunden wird, nur die Message selbst optimieren
      return {
        contextText: fullEmailText,
        markedSection: customMessage
      };
    }
    
    // Markiere den zu optimierenden Bereich
    const beforeText = fullEmailText.substring(0, customMessagePosition);
    const afterText = fullEmailText.substring(customMessagePosition + customMessage.length);
    
    // Erstelle den markierten Text für OpenAI
    const markedText = `${beforeText}[OPTIMIZE_START]${customMessage}[OPTIMIZE_END]${afterText}`;
    
    return {
      contextText: markedText,
      markedSection: customMessage
    };
  }

  /**
   * Fügt die optimierte Custom Message wieder in den E-Mail-Text ein
   */
  mergeOptimizedText(
    originalFullText: string,
    originalCustomMessage: string,
    optimizedCustomMessage: string
  ): string {
    return originalFullText.replace(originalCustomMessage, optimizedCustomMessage);
  }

  /**
   * Validiert die E-Mail-Vorschau auf Vollständigkeit
   */
  validatePreview(preview: EmailPreview): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!preview.subject || preview.subject.trim().length === 0) {
      errors.push('Betreff fehlt');
    }
    
    if (!preview.bodyHtml || preview.bodyHtml.trim().length === 0) {
      errors.push('E-Mail-Inhalt (HTML) fehlt');
    }
    
    if (!preview.bodyText || preview.bodyText.trim().length === 0) {
      errors.push('E-Mail-Inhalt (Text) fehlt');
    }
    
    if (!preview.to || !preview.to.includes('@')) {
      errors.push('Ungültige Empfänger-Adresse');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Singleton Instance
export const emailPreviewService = new EmailPreviewService();